import type { ReactNode } from 'react';
import { ScreenLink as Link } from './permissions/ScreenLink';
import { useTranslation } from 'react-i18next';
import { useInternalOrgId } from '../hooks/useInternalOrg';
import { customerDisplayName, organizationHref } from '../utils/internalOrgPage';

/**
 * P-33c (D-188) — a link to a customer's page. The company (OTE) has no
 * customer page: its link becomes the plain text "OTE, עבודה פנימית".
 */
export default function OrgLink({ orgId, name, className, children }: {
  orgId: string;
  name?: string | null;
  className?: string;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const internalOrgId = useInternalOrgId();
  const href = organizationHref(orgId, internalOrgId);
  if (!href) return <bdi>{t('engagementChain.internalWork')}</bdi>;
  return <Link to={href} className={className}>{children ?? <bdi>{name}</bdi>}</Link>;
}

/**
 * T-56 (D-188) — the customer's NAME as plain text in a list column (tasks,
 * work logs, work cycles, engagements): the company's row reads
 * "OTE, עבודה פנימית" (never "OTE", never a link).
 */
export function OrgName({ orgId, name }: { orgId?: string | null; name?: string | null }) {
  const { t } = useTranslation();
  const internalOrgId = useInternalOrgId();
  return <>{customerDisplayName(orgId, name, internalOrgId, t('engagementChain.internalWork'))}</>;
}

/** Breadcrumb / fact version of the same rule (the page already holds the hook). */
export function orgCrumb(
  orgId: string,
  name: string,
  internalOrgId: string | null | undefined,
  internalLabel: string
): { label: string; href?: string } {
  const href = organizationHref(orgId, internalOrgId);
  return href ? { label: name, href } : { label: internalLabel };
}
